import { createHash } from 'crypto'
import * as fs from 'fs'
import * as path from 'path'
import COS from 'cos-nodejs-sdk-v5'
import { type LayerVersionInfo, type LayerVersionSimple } from 'tencentcloud-sdk-nodejs-scf'
import { type ReleaseOptions } from '../index'
import { getCommitInfo } from '../utils/git'
import { sanitizeDescription } from '../utils/description'
import { archiveFolderToBase64 } from '../utils/zip'
import { createFunctionContext, waitForFunctionActive, waitForLayerActive } from './scf'

const LAYER_UPLOAD_TIMEOUT_MS = 60 * 1000
const LAYER_UPLOAD_SLICE_SIZE = 5 * 1024 * 1024
const LAYER_UPLOAD_CHUNK_SIZE = 8 * 1024 * 1024
const LAYER_UPLOAD_PROGRESS_INTERVAL_MS = 5 * 1000
const LAYER_UPLOAD_LOG_INTERVAL_MS = 15 * 1000

interface LayerArchive {
  filePath: string
  size: number
  digest: string
  objectKey: string
}

export interface ReleaseResult {
  functionVersion: string
  layerVersion: number
  layerObjectKey: string
}

export async function release(directory: string, options: ReleaseOptions): Promise<ReleaseResult> {
  if (!fs.existsSync(directory)) {
    throw new Error('Directory not found.')
  }

  assertReleaseOptions(options)

  const context = createFunctionContext(options)
  const replaceLayerName = options.replaceLayerName || options.layerName
  const layerArchive = await readLayerArchive(options.layerZip, options.layerPrefix)
  const layerRegion = options.layerRegion || options.region
  const description = getReleaseDescription()

  const base64 = await archiveFolderToBase64({ entry: directory })

  await uploadLayerArchive(options, layerRegion, layerArchive)

  console.log('Start publishing layer version...')
  const publishLayerResponse = await context.client.PublishLayerVersion({
    LayerName: options.layerName,
    CompatibleRuntimes: [options.layerRuntime],
    Content: {
      CosBucketName: getScfBucketName(options.layerBucket),
      CosObjectName: `/${layerArchive.objectKey}`,
      CosBucketRegion: layerRegion
    },
    Description: `${description} layer=${layerArchive.digest.slice(0, 12)}`.slice(0, 256)
  })

  const layerVersion = publishLayerResponse.LayerVersion
  if (layerVersion === undefined) {
    throw new Error('PublishLayerVersion did not return a layer version')
  }

  console.log('PublishLayerVersion', publishLayerResponse.RequestId)
  await waitForLayerActive(context.client, options.layerName, layerVersion)

  await waitForFunctionActive(context)
  const functionInfo = await context.client.GetFunction({
    FunctionName: context.functionName,
    Namespace: context.namespace,
    Qualifier: '$LATEST',
    ShowCode: 'FALSE'
  })
  const layers = replaceLayerVersion(
    functionInfo.Layers,
    replaceLayerName,
    options.layerName,
    layerVersion
  )

  console.log('Start replacing function layer...')
  console.log('Next function layers:', JSON.stringify(layers))
  const updateConfigurationResponse = await context.client.UpdateFunctionConfiguration({
    FunctionName: context.functionName,
    Namespace: context.namespace,
    Layers: layers
  })
  console.log('UpdateFunctionConfiguration', updateConfigurationResponse.RequestId)
  await waitForFunctionActive(context)

  console.log('Start updating function code...')
  const updateCodeResponse = await context.client.UpdateFunctionCode({
    FunctionName: context.functionName,
    Namespace: context.namespace,
    ZipFile: base64
  })
  console.log('UpdateFunctionCode', updateCodeResponse.RequestId)
  await waitForFunctionActive(context)

  console.log('Start publishing function version...')
  const publishFunctionResponse = await context.client.PublishVersion({
    FunctionName: context.functionName,
    Namespace: context.namespace,
    Description: description
  })
  const functionVersion = publishFunctionResponse.FunctionVersion
  if (!functionVersion) {
    throw new Error('PublishVersion did not return a function version')
  }

  console.log('PublishVersion', publishFunctionResponse.RequestId)
  await waitForFunctionActive(context)

  const result = {
    functionVersion,
    layerVersion,
    layerObjectKey: layerArchive.objectKey
  }
  console.log('Release finished:', JSON.stringify(result))
  return result
}

function assertReleaseOptions(options: ReleaseOptions) {
  const requiredOptions: Array<[string, string | undefined]> = [
    ['layerZip', options.layerZip],
    ['layerName', options.layerName],
    ['layerBucket', options.layerBucket],
    ['layerPrefix', options.layerPrefix],
    ['layerRuntime', options.layerRuntime]
  ]

  for (const [name, value] of requiredOptions) {
    if (!value?.trim()) {
      throw new Error(`Missing required option: ${name}`)
    }
  }
}

async function readLayerArchive(layerZip: string, layerPrefix: string): Promise<LayerArchive> {
  const resolvedPath = path.resolve(layerZip)
  if (!fs.existsSync(resolvedPath)) {
    throw new Error(`Layer archive not found: ${resolvedPath}`)
  }

  const fileStats = fs.statSync(resolvedPath)
  if (!fileStats.isFile() || fileStats.size === 0) {
    throw new Error(`Layer archive is empty: ${resolvedPath}`)
  }

  console.log(`Calculating SHA-256 for Layer archive (${fileStats.size} bytes)...`)
  const digest = await calculateFileDigest(resolvedPath)
  const normalizedPrefix = layerPrefix.replace(/^\/+|\/+$/g, '')
  const objectKey = normalizedPrefix ? `${normalizedPrefix}/${digest}.zip` : `${digest}.zip`

  return { filePath: resolvedPath, size: fileStats.size, digest, objectKey }
}

async function calculateFileDigest(filePath: string): Promise<string> {
  const hash = createHash('sha256')

  for await (const chunk of fs.createReadStream(filePath)) {
    hash.update(chunk)
  }

  return hash.digest('hex')
}

async function uploadLayerArchive(
  options: ReleaseOptions,
  layerRegion: string,
  layerArchive: LayerArchive
) {
  const cos = new COS({
    SecretId: options.secretId,
    SecretKey: options.secretKey,
    Timeout: LAYER_UPLOAD_TIMEOUT_MS,
    ChunkRetryTimes: 2,
    ChunkParallelLimit: 3,
    ProgressInterval: LAYER_UPLOAD_PROGRESS_INTERVAL_MS,
    UseAccelerate: options.layerUseAccelerate
  })

  let lastProgressPercent = -5
  let lastProgressLogAt = 0
  const uploadDomain = options.layerUseAccelerate
    ? `${options.layerBucket}.cos.accelerate.myqcloud.com`
    : `${options.layerBucket}.cos.${layerRegion}.myqcloud.com`

  console.log(
    `Uploading layer archive (${layerArchive.size} bytes) via ${uploadDomain}: cos://${options.layerBucket}/${layerArchive.objectKey}`
  )
  const uploadResult = await cos.uploadFile({
    Bucket: options.layerBucket,
    Region: layerRegion,
    Key: layerArchive.objectKey,
    FilePath: layerArchive.filePath,
    SliceSize: LAYER_UPLOAD_SLICE_SIZE,
    ChunkSize: LAYER_UPLOAD_CHUNK_SIZE,
    ContentType: 'application/zip',
    onTaskReady(taskId) {
      console.log('Layer upload task:', taskId)
    },
    onProgress(progress) {
      const progressPercent = Math.floor(progress.percent * 100)
      const now = Date.now()
      const shouldLog =
        progressPercent >= 100 ||
        progressPercent >= lastProgressPercent + 5 ||
        now - lastProgressLogAt >= LAYER_UPLOAD_LOG_INTERVAL_MS

      if (!shouldLog) {
        return
      }

      lastProgressPercent = progressPercent
      lastProgressLogAt = now
      console.log(
        `Layer upload progress: ${progressPercent}% (${formatBytes(progress.loaded)}/${formatBytes(progress.total)} at ${formatBytes(progress.speed)}/s)`
      )
    }
  })
  console.log('Layer archive uploaded:', uploadResult.ETag ?? 'ETag unavailable')
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB']
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** unitIndex
  const precision = unitIndex === 0 ? 0 : 1

  return `${value.toFixed(precision)} ${units[unitIndex]}`
}

function getScfBucketName(bucket: string): string {
  const match = bucket.match(/^(.*)-\d+$/)
  if (!match?.[1]) {
    throw new Error(`Layer bucket must include its AppId suffix: ${bucket}`)
  }

  return match[1]
}

function replaceLayerVersion(
  currentLayers: Array<LayerVersionInfo> | undefined,
  replaceLayerName: string,
  layerName: string,
  layerVersion: number
): Array<LayerVersionSimple> {
  const layers = (currentLayers ?? []).map((layer) => {
    if (!layer.LayerName || layer.LayerVersion === undefined) {
      throw new Error('GetFunction returned an incomplete layer reference')
    }

    return {
      LayerName: layer.LayerName,
      LayerVersion: layer.LayerVersion
    }
  })
  const matches = layers.filter((layer) => layer.LayerName === replaceLayerName)

  if (matches.length > 1) {
    throw new Error(`Function has duplicate layer bindings for: ${replaceLayerName}`)
  }

  if (matches.length === 0) {
    return [...layers, { LayerName: layerName, LayerVersion: layerVersion }]
  }

  return layers.map((layer) =>
    layer.LayerName === replaceLayerName ? { LayerName: layerName, LayerVersion: layerVersion } : layer
  )
}

function getReleaseDescription(): string {
  const [hash, message] = getCommitInfo()
  // Description 字段有字符白名单限制（em-dash 等会被腾讯云拒绝），必须净化后再使用
  return sanitizeDescription([hash, message].join(' '))
}